import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import SearchableDropdownTypeEnum from '@/Enums/SearchableDropdownTypeEnum'
import { Spinner } from '../ui/spinner'
import Constant from '@/utils/Constant'
import { useDebounce } from 'use-debounce'
import { useAuth } from '@/Context/AuthContext'
import { checkPermission } from '@/utils/helper'
import RequiredPermission, { GlobleHideAndDisabledFieldAndButtonRole } from '@/assets/data/RequiredPermission'
import CommonApiService from '@/Service/CommonApiService'
import UserService from '@/Service/UserService'

const SearchableDropdown = ({
    options,
    label,
    selectedVal,
    handleChange,
    placeholder,
    field,
    className,
    type = '',
    name = '',
    searchFilter = {},
    subApiCall = '',
    subApiDefaultFilter = {},
    onClick,
    nodata_category,
}) => {
    const { userInfo } = useAuth()
    const [query, setQuery] = useState('')
    const [isOpen, setIsOpen] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const [filterData, setFilterData] = useState([])
    const [searchableQuery] = useDebounce(query, Constant.DEBOUNCE_DELAY)
    const [selectedIndex, setSelectedIndex] = useState(-1)
    const [keyDirection, setKeyDirection] = useState(-1)
    const optionsContainerRef = useRef(null)
    const optionRefs = useRef([])
    const inputRef = useRef(null)

    useEffect(() => {
        optionRefs.current = optionRefs.current.slice(0, filterData.length)
    }, [filterData])

    useEffect(() => {
        setFilterData(options)
    }, [options])

    useEffect(() => {
        document.addEventListener('click', toggle)
        return () => document.removeEventListener('click', toggle)
    }, [])

    const selectOption = (e, option) => {
        inputRef.current.focus()
        setQuery(() => '')
        if (type in SearchableDropdownTypeEnum || type == 'GET_OBJ') {
            handleChange(option)
        } else {
            handleChange(option[label])
        }
        setIsOpen((isOpen) => !isOpen)
        if (onClick) {
            onClick(e)
        }
    }

    function toggle(e) {
        setIsOpen(e && e.target === inputRef.current)
        if (onClick) {
            onClick(e)
        }
    }

    const handleFocus = () => {
        if (!isOpen) {
            setIsOpen(true)
        }
    }
    const handleBlur = () => {
        setQuery('')
        setTimeout(() => {
            setIsOpen(false)
        }, 200)
    }
    const handleKeyDown = (event) => {
        if (!isOpen) {
            setIsOpen(event && event.target === inputRef.current)
        }
        const selectedOptionRef = optionRefs.current.length > 0 && optionRefs.current[selectedIndex]
        const optionsContainerRef2 = optionsContainerRef.current

        if (event.key === 'ArrowDown') {
            event.preventDefault()
            if (keyDirection !== -1) {
                setKeyDirection(-1)
            }
            setSelectedIndex((prevIndex) => {
                const next = Math.min(prevIndex + 1, filterData.length - 1)
                return next
            })
        } else if (event.key === 'ArrowUp') {
            event.preventDefault()
            if (keyDirection !== 1) {
                setKeyDirection(1)
            }
            setSelectedIndex((prevIndex) => {
                const next = Math.max(prevIndex - 1, 0)
                return next
            })
        } else if (event.key === 'Enter') {
            event.preventDefault()
            if (selectedIndex >= 0 && filterData[selectedIndex]) {
                selectOption(event, filterData[selectedIndex])
            }
        } else if (event.key === 'Tab') {
            if (!isOpen) {
                setIsOpen(true)
            }
        }
        if (selectedOptionRef && optionsContainerRef2) {
            selectedOptionRef.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
            })
        }
    }

    const getDisplayValue = () => {
        if (query) return query
        if (selectedVal) {
            return selectedVal
        }
        return ''
    }

    useEffect(() => {
        filter(options)
    }, [searchableQuery])

    const filter = async (options) => {
        if (query === '') {
            setFilterData(options)
            setSelectedIndex(0)
            return
        }
        setIsLoading(true)
        switch (placeholder) {
            case 'Roles':
                await getRoles(query)
                break
            case 'Employee Email':
                await getEmployee(query)
                break
            default:
                defaultSearch()
                break
        }
        setIsLoading(false)
        setSelectedIndex(0)
    }

    const defaultSearch = () => {
        let defaultSearchedData =
            options.length > 0 &&
            options.filter((option) => {
                return option[label]?.toLowerCase().indexOf(query.toLowerCase()) > -1
            })
        setFilterData(defaultSearchedData)
    }
    const getRoles = async (query) => {
        try {
            const resp = await CommonApiService.getRoles({ role: query })
            if (resp.data.success) {
                setFilterData(resp.data.data)
                return
            }
        } catch (err) {}
        return []
    }
    const getEmployee = async (query) => {
        try {
            const resp = await UserService.getUsersByRole({ email: query, name: query, ...searchFilter })
            if (resp.data.success) {
                setFilterData(resp.data.data)
                return
            }
        } catch (err) {}
        return []
    }

    return (
        <div className="dropdownx">
            <div className="control">
                <div className="selected-value">
                    <input
                        ref={inputRef}
                        type="text"
                        {...field}
                        value={getDisplayValue()}
                        name="searchTerm"
                        onChange={(e) => {
                            setQuery(e.target.value)
                            handleChange('')
                        }}
                        onKeyDown={handleKeyDown}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                        placeholder={placeholder}
                        onClick={toggle}
                        autoComplete="off"
                        className={cn(
                            `flex w-full rounded-md bg-transparent px-3 py-1 text-base transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-neutral-950 placeholder:text-neutral-500 focus-visible:outline-none focus-visible:ring-1  disabled:cursor-not-allowed disabled:opacity-50 md:text-sm dark:border-neutral-800 dark:file:text-neutral-50 dark:placeholder:text-neutral-400 dark:focus-visible:ring-neutral-300 border shadow-sm text-[13px] input-outline-shadow ${className}`
                        )}
                        // disabled={!['notForDisable'].includes(name) && !checkPermission(GlobleHideAndDisabledFieldAndButtonRole, userInfo.roles)}
                    />
                </div>
            </div>
            {nodata_category ? (
                ''
            ) : (
                <div className={`options bg-white shadow-md border rounded-md ${isOpen ? 'open' : ''}`} ref={optionsContainerRef}>
                    {options.length > 0 && filterData.length > 0 ? (
                        filterData.map((option, index) => {
                            return (
                                <div
                                    ref={(el) => (optionRefs.current[index + keyDirection] = el)} // Add ref to each option
                                    onClick={(e) => selectOption(e, option)}
                                    className={cn(
                                        'px-4 py-2 cursor-pointer hover:bg-blue-100 text-[13px]',
                                        option[label] === selectedVal && 'bg-blue-200 font-semibold',
                                        index === selectedIndex && 'bg-blue-100'
                                    )}
                                    key={`${index}`}
                                >
                                    {placeholder === 'Employee Email' ||
                                    placeholder === 'Coder Name' ||
                                    placeholder === 'QA Name' ||
                                    placeholder === 'Prod TL' ||
                                    placeholder === 'QA TL' ? (
                                        <div className="flex flex-col text-left">
                                            <span className="font-bold text-[13px]">{option['full_name']}</span>
                                            <span className="text-[13px]">{option['name']}</span>
                                        </div>
                                    ) : (
                                        <span className="text-[15px] text-left">{option[label] !== 'Primary Diagnosis' && option[label]}</span>
                                    )}
                                </div>
                            )
                        })
                    ) : isLoading ? (
                        <div className="px-4 py-2 text-center">
                            <Spinner size="small" />
                        </div>
                    ) : (
                        <div className="px-4 py-2 text-center text-gray-500 text-[13px]">No data found</div>
                    )}
                </div>
            )}
        </div>
    )
}

export default SearchableDropdown
